import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dashboard, Logout, Menu as MenuIcon, Notifications, Person } from '@mui/icons-material';
import { AppBar, Avatar, Badge, Box, Divider, IconButton, ListItemIcon, Menu, MenuItem, Toolbar, Typography } from '@mui/material';
import Images from 'assets/images/Images';
import Colors from 'assets/style/Colors';
import { CircleLoading } from 'components/loaders/Loaders';
import MembershipBadge from 'components/chip/MembershipBadge';
import { useAuth } from 'context/UseContext';
import { usePusher } from 'hooks/usePusher';
import notificationTypes from 'data/notification_types.json'
import moment from 'moment';
import { DropShipperTypeId, GuestBuyerTypeId, ManufacturerTypeId, SupplierTypeId } from 'utils';

function DropDown({ anchorEl, openDropdown, handleClose }) {

  const { userLogout } = useAuth();
  const navigate = useNavigate();

  return (
    <Menu
      anchorEl={anchorEl}
      open={openDropdown}
      onClose={handleClose}
      onClick={handleClose}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          mt: 1.5,
          '.MuiSvgIcon-root': {
            width: 20,
            height: 20,
            ml: 0.5,
            mr: 0.5,
          },
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      }}
    >
      <MenuItem onClick={() => navigate('/')}>
        <ListItemIcon>
          <Dashboard />
        </ListItemIcon>
        Dashboard
      </MenuItem>
      <MenuItem onClick={() => navigate('/profile')}>
        <ListItemIcon>
          <Person />
        </ListItemIcon>
        Profile
      </MenuItem>
      <Divider sx={{ my: 0.5 }} />
      <MenuItem onClick={() => userLogout()}>
        <ListItemIcon>
          <Logout />
        </ListItemIcon>
        Logout
      </MenuItem>
    </Menu>
  )
}

function Header({ onMenuClick }) {

  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifications, unReadCount, handleMarkAsRead } = usePusher();

  const [loading, setLoading] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const openDropdown = Boolean(anchorEl);

  const [notifyAnchor, setNotifyAnchor] = useState(null);
  const openNotificationList = Boolean(notifyAnchor);

  const handleClickNotification = (data) => {
    setNotifyAnchor(null);
    const formData = new FormData()
    formData.append('notification_ids', JSON.stringify([data?.id]))
    handleMarkAsRead(formData)
    if (data?.type === 'ticketCreatedAdmin' || data?.type === 'ticketReplyUser') {
      if (data?.type_id) navigate(`/tickets/${data.type_id}`)
      return
    }
    const route = notificationTypes.find(item => item.type === data?.type)
    if (route?.type === 'userRegistration' || route?.type === 'userRegisteredAdmin') {
      const usersId = [{ id: 1, name: 'users' }, { id: ManufacturerTypeId, name: 'manufacturers' }, { id: DropShipperTypeId, name: 'dropshippers' }, { id: SupplierTypeId, name: 'suppliers' }, { id: GuestBuyerTypeId, name: 'guest-buyers' }]
      const registerUser = usersId.find(e => e.id == data?.type_id)?.name
      navigate(`${route?.route}/${registerUser}`)
    } else {
      navigate(route?.route)
    }
  };

  const handleMarkAllAsRead = () => {
    setNotifyAnchor(null);
    const formData = new FormData()
    formData.append('mark_all', 1)
    handleMarkAsRead(formData)
  }

  return (
    <AppBar elevation={0} position="static" sx={{ bgcolor: Colors.white, }}>
      <Toolbar sx={{ px: { xl: 6, xs: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={onMenuClick}
            sx={{ mr: 1, display: { md: 'none' }, color: Colors.textPrimary }}
          >
            <MenuIcon />
          </IconButton>
          <Box component="img" src={Images.logo} sx={{ height: '70px' }} />
          {/* <Typography variant="h3" sx={{ color: Colors.textPrimary }}>
            HASHTAG
          </Typography> */}
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton aria-label="notification" sx={{ mx: 1 }} onClick={(e) => { setNotifyAnchor(e.currentTarget) }}>
            <Badge badgeContent={unReadCount} color="danger">
              <Notifications />
            </Badge>
          </IconButton>

          {/* ========== Notification Dropdown ========== */}
          <Menu
            anchorEl={notifyAnchor}
            open={openNotificationList}
            onClose={() => setNotifyAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              elevation: 0,
              sx: {
                width: '390px',
                overflow: 'auto',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                borderRadius: '12px',
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: '16px', py: 1 }}>
              <Typography variant="subtitle1">
                Notifications
              </Typography>
              <Typography variant="body2" sx={{ cursor: 'pointer' }} onClick={() => handleMarkAllAsRead()}>
                Mark all as read
              </Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box>
              {loading ? (
                <CircleLoading />
              ) : (
                notifications?.length > 0 ? (
                  notifications.map((item, index) => (
                    <MenuItem key={index}
                      onClick={() => handleClickNotification(item)}
                      sx={{
                        borderBottom: `1px solid ${Colors.smokeyGrey + '4D'}`,
                        bgcolor: item?.seen === 1 ? 'transparent' : Colors.textPrimary + '40',
                        py: 2,
                        my: 0.3,
                        gap: '10px',
                        alignItems: 'start',
                        ':hover': {
                          bgcolor: item?.seen === 1 ? 'transparent' : Colors.textPrimary + '40'
                        }
                      }}
                    >
                      {/* <Box sx={{ width: '100%', display: 'flex', gap: '5px', alignItems: 'center' }}> */}
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          border: `1px solid ${Colors.primary}`,
                          '.MuiAvatar-img': { objectFit: 'contain' }
                        }}
                      />
                      <Box sx={{ whiteSpace: 'break-spaces' }}>
                        <Typography variant="body2" >
                          {item.text}
                        </Typography>
                        <MembershipBadge tier={item?.actor_tier} sx={{ mt: 0.5 }} />
                        <Typography variant="caption" sx={{ color: Colors.released }} >
                          {moment(item?.created_at).format('DD/MM/YYYY HH:mm A')}
                        </Typography>
                      </Box>
                      {/* </Box> */}
                      {/* <Box>
                        {item?.seen === 0 && (
                          <MarkEmailUnread
                            onClick={() => handleMarkAsRead([item?.id])}
                            sx={{ cursor: 'pointer' }}
                          />
                        )}
                      </Box> */}
                    </MenuItem>
                  ))) : (
                  <Typography variant="body1" sx={{ py: 2, px: 1, textAlign: 'center' }} >
                    You have no notification.
                  </Typography>
                ))}
            </Box>
          </Menu>

          <Box sx={{ cursor: 'pointer' }} onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar
              src={user?.image}
              sx={{
                width: 30,
                height: 30,
                border: `1px solid ${Colors.primary}`,
                '.MuiAvatar-img': { objectFit: 'contain' }
              }}
            />
          </Box>
        </Box>
        <DropDown anchorEl={anchorEl} openDropdown={openDropdown} handleClose={() => setAnchorEl(null)} />
      </Toolbar>
    </AppBar>
  );
}

export default Header;